import { useCallback, useMemo } from 'react';

import { dropAt } from '../../../utils';
import useOrganizationSettings from '../shared/useOrganizationSettings';
import type { CocoapodsSource } from '../../../../typings/auth';
import type { OrganizationSettingsEndpoint } from '../shared/useOrganizationSettings';

/**
 * Hook for fetching the Cocoapods organization settings.
 *
 * "Why is this separate from useLanguageSettings?"
 *
 * It's because unlike other settings sections (which treat the settings as a single object),
 * the Cocoapods settings are an array of strings. Because of this, we need to make use of
 * the `onSaveSettingsWithOverride` method from useOrganizationSettings, which allows us to
 * directly pass in our settings instead of having to merge them with the existing settings.
 *
 * In addition, this hook exposes nice helpers for add/remove/update on the list of sources.
 */
function useCocoapodsSettings(orgId: number) {
  const {
    settings: sources,
    isLoading,
    errors,
    onUpdateSettings,
    onSaveSettingsWithOverride,
    isSaveButtonDisabled,
  } = useOrganizationSettings<CocoapodsSource[]>({
    endpoint: useMemo(() =>
      `/api/organizations/${orgId}/settings/languages/pod` as OrganizationSettingsEndpoint, [orgId]),
    settingsSection: 'CocoaPods',
  });

  //  Helper for removing a source at the given index.
  const onRemoveSource = useCallback((index: number) =>
    onUpdateSettings(dropAt(sources || [], index)),
  [onUpdateSettings, sources]);

  //  Helper for updating a source at the given index.
  const onUpdateSource = useCallback((index: number, value: string) =>
    // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `source` is settings data owned by the settings hook
    onUpdateSettings((sources || []).map((source, currIndex) => {
      return (currIndex === index)
        ? {
            ...source,
            url: value,
          }
        : source;
    })),
  [onUpdateSettings, sources]);

  const onAddSource = useCallback(() =>
    onUpdateSettings([
      ...(sources || []),
      { url: '' },
    ]),
  [onUpdateSettings, sources]);

  const onSaveSettings = useCallback(() =>
    onSaveSettingsWithOverride(sources?.map(s => s.url) as unknown as CocoapodsSource[]),
  [onSaveSettingsWithOverride, sources]);

  return {
    sources,
    isLoading: isLoading.fetch,
    isSaving: isLoading.save,
    errors,
    onRemoveSource,
    onAddSource,
    onUpdateSource,
    onSaveSettings,
    isSaveButtonDisabled,
  };
}

export default useCocoapodsSettings;
